/**
 * Security Service - API client for the student pickup QR/security
 * module: daily QR dispatch, gate verification, arrival status,
 * and live bus/collector location tracking
 */
import api, { API_URL } from '../utils/api';

const securityService = {
  // ---- Students & arrival status ----

  listStudents: async (schoolId) => {
    try {
      return await api.get('/api/security/students', { params: { school_id: schoolId } });
    } catch (error) {
      console.error('Error fetching security students:', error);
      throw error;
    }
  },

  getStudentStatus: async (studentId) => {
    try {
      return await api.get(`/api/security/students/${studentId}/status`);
    } catch (error) {
      console.error('Error fetching student security status:', error);
      throw error;
    }
  },

  updateSecurityProfile: async (studentId, data) => {
    try {
      return await api.patch(`/api/security/profiles/${studentId}`, data);
    } catch (error) {
      console.error('Error updating security profile:', error);
      throw error;
    }
  },

  confirmStudentArrival: async (studentId) => {
    try {
      return await api.post(`/api/security/students/${studentId}/confirm`);
    } catch (error) {
      console.error('Error confirming student arrival:', error);
      throw error;
    }
  },

  addParentNote: async (studentId, text) => {
    try {
      return await api.post(`/api/security/students/${studentId}/notes`, { text, is_confirmation: false });
    } catch (error) {
      console.error('Error adding parent note:', error);
      throw error;
    }
  },

  // ---- "On My Way" advance pickup notice ----

  sendOnMyWay: async (studentId, etaMinutes) => {
    try {
      return await api.post(`/api/security/students/${studentId}/on-my-way`, { eta_minutes: etaMinutes || undefined });
    } catch (error) {
      console.error('Error sending on-my-way notice:', error);
      throw error;
    }
  },

  cancelOnMyWay: async (studentId) => {
    try {
      return await api.delete(`/api/security/students/${studentId}/on-my-way`);
    } catch (error) {
      console.error('Error cancelling on-my-way notice:', error);
      throw error;
    }
  },

  getPickupQueue: async (schoolId) => {
    try {
      return await api.get('/api/security/pickup-queue', { params: { school_id: schoolId } });
    } catch (error) {
      console.error('Error fetching pickup queue:', error);
      throw error;
    }
  },

  // ---- Campus-exit hold ----

  setReleaseHold: async (studentId, reason) => {
    try {
      return await api.post(`/api/security/students/${studentId}/release-hold`, { reason });
    } catch (error) {
      console.error('Error setting release hold:', error);
      throw error;
    }
  },

  clearReleaseHold: async (studentId) => {
    try {
      return await api.delete(`/api/security/students/${studentId}/release-hold`);
    } catch (error) {
      console.error('Error clearing release hold:', error);
      throw error;
    }
  },

  // ---- Court-order pickup restriction ----

  setPickupRestriction: async (studentId, parentId, isRestricted, reason) => {
    try {
      return await api.put(`/api/security/students/${studentId}/parents/${parentId}/pickup-restriction`, {
        is_restricted: isRestricted, reason: reason || undefined,
      });
    } catch (error) {
      console.error('Error setting pickup restriction:', error);
      throw error;
    }
  },

  // ---- Authorized pickup persons ----

  listPickupPersons: async (studentId) => {
    try {
      return await api.get(`/api/security/students/${studentId}/pickup-persons`);
    } catch (error) {
      console.error('Error fetching pickup persons:', error);
      throw error;
    }
  },

  addPickupPerson: async (studentId, data) => {
    try {
      return await api.post(`/api/security/students/${studentId}/pickup-persons`, data);
    } catch (error) {
      console.error('Error adding pickup person:', error);
      throw error;
    }
  },

  // School-wide roster (staff-only) — used by the ID Cards page's person
  // picker, unlike listPickupPersons above which is scoped to one student.
  listAllPickupPersons: async (params) => {
    try {
      return await api.get('/api/security/pickup-persons', { params });
    } catch (error) {
      console.error('Error fetching all pickup persons:', error);
      throw error;
    }
  },

  uploadPickupPersonPhoto: async (personId, formData) => {
    try {
      return await api.post(`/api/security/pickup-persons/${personId}/photo`, formData);
    } catch (error) {
      console.error('Error uploading pickup person photo:', error);
      throw error;
    }
  },

  updatePickupPerson: async (personId, data) => {
    try {
      return await api.patch(`/api/security/pickup-persons/${personId}`, data);
    } catch (error) {
      console.error('Error updating pickup person:', error);
      throw error;
    }
  },

  deletePickupPerson: async (personId) => {
    try {
      return await api.delete(`/api/security/pickup-persons/${personId}`);
    } catch (error) {
      console.error('Error deleting pickup person:', error);
      throw error;
    }
  },

  // ---- On-campus location ----

  updateStudentLocation: async (studentId, location) => {
    try {
      return await api.put(`/api/security/students/${studentId}/location`, { location });
    } catch (error) {
      console.error('Error updating student location:', error);
      throw error;
    }
  },

  getLocationHistory: async (studentId, limit) => {
    try {
      return await api.get(`/api/security/students/${studentId}/location-history`, { params: limit ? { limit } : {} });
    } catch (error) {
      console.error('Error fetching location history:', error);
      throw error;
    }
  },

  getLocationBoard: async (schoolId) => {
    try {
      return await api.get('/api/security/location-board', { params: { school_id: schoolId } });
    } catch (error) {
      console.error('Error fetching location board:', error);
      throw error;
    }
  },

  // ---- QR dispatch & verification ----

  getQrStatus: async (schoolId) => {
    try {
      return await api.get('/api/security/qr/status', { params: { school_id: schoolId } });
    } catch (error) {
      console.error('Error fetching QR status:', error);
      throw error;
    }
  },

  // EventSource can't attach an Authorization header, so the auth token travels
  // as a query param here — the backend's /events/stream endpoint accepts it that way.
  // Carries qr_dispatch and bus_location events (collector_location stays poll-only, see backend docstring).
  getEventsStreamUrl: (schoolId) =>
    `${API_URL}/api/security/events/stream?school_id=${encodeURIComponent(schoolId)}&token=${encodeURIComponent(api.getToken() || '')}`,

  generateDailyQr: async (schoolId, idempotencyKey) => {
    try {
      return await api.post('/api/security/qr/generate-daily', { school_id: schoolId, idempotency_key: idempotencyKey });
    } catch (error) {
      console.error('Error generating daily QR codes:', error);
      throw error;
    }
  },

  getStudentQr: async (studentId) => {
    try {
      return await api.get(`/api/security/qr/student/${studentId}`);
    } catch (error) {
      console.error('Error fetching student QR:', error);
      throw error;
    }
  },

  emailStudentQr: async (studentId, toEmail) => {
    try {
      return await api.post(`/api/security/qr/student/${studentId}/email`, toEmail ? { to_email: toEmail } : undefined);
    } catch (error) {
      console.error('Error emailing student QR:', error);
      throw error;
    }
  },

  verifyQr: async (token, gateLat, gateLng) => {
    try {
      return await api.post('/api/security/qr/verify', {
        token,
        gate_lat: gateLat ?? null,
        gate_lng: gateLng ?? null,
      });
    } catch (error) {
      console.error('Error verifying QR token:', error);
      throw error;
    }
  },

  // ---- Transport dispatch & live location ----

  dispatchTransportRoute: async (routeId, schoolId) => {
    try {
      return await api.post(`/api/security/transport/${routeId}/dispatch`, { school_id: schoolId });
    } catch (error) {
      console.error('Error dispatching transport route:', error);
      throw error;
    }
  },

  activateTransportSession: async (routeId) => {
    try {
      return await api.post(`/api/security/qr/transport/${routeId}/activate`);
    } catch (error) {
      console.error('Error activating transport tracking session:', error);
      throw error;
    }
  },

  getTransportLocation: async (routeId) => {
    try {
      return await api.get(`/api/security/transport/${routeId}/location`);
    } catch (error) {
      console.error('Error fetching transport location:', error);
      throw error;
    }
  },

  postTransportLocation: async (routeId, data) => {
    try {
      return await api.post(`/api/security/transport/${routeId}/location`, data);
    } catch (error) {
      console.error('Error posting transport location:', error);
      throw error;
    }
  },

  getCollectorLiveLocation: async (sessionToken) => {
    try {
      return await api.get(`/api/security/collector/${sessionToken}/location/live`);
    } catch (error) {
      console.error('Error fetching collector location:', error);
      throw error;
    }
  },

  // ---- Admin overview ----

  getAdminOverview: async (schoolId) => {
    try {
      return await api.get('/api/security/admin/overview', { params: { school_id: schoolId } });
    } catch (error) {
      console.error('Error fetching security admin overview:', error);
      throw error;
    }
  },

  getScanLogs: async (schoolId, { days, result, limit, offset } = {}) => {
    try {
      return await api.get('/api/security/admin/scan-logs', {
        params: { school_id: schoolId, days, result, limit, offset },
      });
    } catch (error) {
      console.error('Error fetching security scan logs:', error);
      throw error;
    }
  }
};

export default securityService;
